import React, { useEffect, useState } from 'react';
import { Sparkles, Plus, Moon, ShieldCheck, ShieldAlert, Shield } from 'lucide-react';
import './communicationsIntelligence.css';
import AdminPageHeader from '../AdminPageHeader';
import KpiBand from './KpiBand';
import InboxScreen from './InboxScreen';
import OutboxScreen from './OutboxScreen';
import TemplatesScreen from './TemplatesScreen';
import AutomationsScreen from './AutomationsScreen';
import AnalyticsScreen from './AnalyticsScreen';
import CreateScreen from './CreateScreen';
import JourneyScreen from './JourneyScreen';
import SafetySettingsModal from './SafetySettingsModal';
import { getOutboundSettings } from '../../../data/api';

// Communications Intelligence — NEW, flagged module. Renders ONLY the inner
// content (title row, KPI band, sub-tabs, screens). The global sidebar + header
// are provided by AdminPage; this never draws its own.
//
// Props mirror the existing hub call site so AdminPage can swap in cleanly:
//   guests, messages, onMessagesChanged.

const SUBTABS = [
  { key: 'inbox', label: 'Guest Communications' },
  { key: 'outbox', label: 'Outbox' },
  { key: 'templates', label: 'Templates' },
  { key: 'automations', label: 'Automations' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'journey', label: 'View Journey' },
];

const TITLES = {
  inbox: ['Guest Communications', 'Milo is managing communications and relationships for your event.'],
  outbox: ['Outbox', 'Track and manage every sent and scheduled communication.'],
  templates: ['Templates', 'Create and manage reusable message templates.'],
  automations: ['Automations', 'Set up intelligent communication workflows.'],
  analytics: ['Communication Analytics', 'Real metrics and Milo predictions.'],
  create: ['Create Communication', 'Design and preview the perfect message.'],
  journey: ['Guest Journey', "See every step of a guest's communication journey."],
};

// Send-safety mode -> pill presentation. Maps the persisted send_mode to a
// label, tone class, and icon so the header pill reflects the live gate state.
const MODE_PILL = {
  hold: { label: 'Hold for Review', tone: 'ci-pill-blue', Icon: Shield },
  test: { label: 'Test Mode', tone: 'ci-pill-green', Icon: ShieldCheck },
  live: { label: 'Live', tone: 'ci-pill-gold', Icon: ShieldCheck },
};

export default function CommunicationsIntelligence({ guests = [], messages = [], onMessagesChanged }) {
  // `screen` is either a sub-tab key (including 'journey', a normal tab) or the
  // full-page 'create' screen. The full-page screen hides the KPI band +
  // sub-tabs; tabs (View Journey included) keep them.
  const [screen, setScreen] = useState('inbox');
  // Optional pre-fill handed to the Create screen (e.g. Outbox "Use again").
  // null = compose from scratch. Cleared whenever we leave Create so the next
  // blank "Create Communication" entry doesn't inherit a stale draft.
  const [createPrefill, setCreatePrefill] = useState(null);

  // Send-safety mode shown in the header pill. Read from the same outbound
  // settings the safety gate enforces; refreshed after the settings modal saves.
  const [sendMode, setSendMode] = useState(null); // null until first load
  const [safetyEnabled, setSafetyEnabled] = useState(true);
  const [modeLoading, setModeLoading] = useState(true);
  const [modeError, setModeError] = useState(false);
  const [safetyOpen, setSafetyOpen] = useState(false);

  const refreshSendMode = () => {
    setModeLoading(true);
    setModeError(false);
    getOutboundSettings()
      .then((s) => {
        setSendMode(['hold', 'test', 'live'].includes(s?.send_mode) ? s.send_mode : 'hold');
        setSafetyEnabled(!!s?.safety_enabled);
      })
      .catch(() => setModeError(true))
      .finally(() => setModeLoading(false));
  };
  useEffect(() => { refreshSendMode(); }, []);

  // Milo ON/OFF (mirrors Event Readiness "quiet mode"). When quiet, the Milo
  // Recommendation hero and the per-guest Milo Insights panel collapse to a
  // slim "resting" affordance. Persisted locally — no backend involved.
  const QUIET_KEY = 'ci_milo_quiet';
  const [miloQuiet, setMiloQuiet] = useState(() => {
    try { return window.localStorage.getItem(QUIET_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(QUIET_KEY, miloQuiet ? '1' : '0'); } catch { /* ignore */ }
  }, [miloQuiet]);

  // Only Create is a full-page screen now; View Journey is a normal tab.
  const isFull = screen === 'create';
  // The brand title is always "Communications Intelligence" (per the approved
  // design — this is the rename of the old "Communications Hub"). The current
  // section name + its description ride along in the subtitle for context.
  const [section, sectionSub] = TITLES[screen] || TITLES.inbox;
  const title = 'Communications Intelligence';
  const sub = `${section} · ${sectionSub}`;

  // Open Create. Pass a prefill payload to seed channel/template/subject
  // (Outbox "Use again"); call with no argument for a blank composition.
  const openCreate = (prefill = null) => { setCreatePrefill(prefill); setScreen('create'); };
  const backToTabs = () => { setCreatePrefill(null); setScreen('inbox'); };

  // Event Readiness hands off comms actions (RSVP reminder / invitation / follow-up /
  // thank-you) by writing sessionStorage('milo_email_template_prefill') then routing
  // here — AdminPage.navigateToModule remaps 'messaging' -> 'communications' when this
  // module is enabled. On mount we consume that prefill ONCE: map the matched template
  // + audience scope + type into a Create prefill and open the builder, so ER no longer
  // dead-ends in the legacy MessagingTab. One-shot read + clear; the legacy path is
  // untouched when this module is off.
  useEffect(() => {
    let raw = null;
    try { raw = window.sessionStorage.getItem('milo_email_template_prefill'); } catch { return undefined; }
    if (!raw) return undefined;
    let payload = null;
    try { payload = JSON.parse(raw); } catch { return undefined; }
    const pf = payload && payload.target && payload.target.prefill;
    if (!pf || typeof pf.template_id !== 'string' || !pf.template_id) return undefined;
    try { window.sessionStorage.removeItem('milo_email_template_prefill'); } catch { /* ignore */ }
    const scope = String(pf.audience_scope || '').toLowerCase();
    const segment = scope.includes('pending') ? 'pending'
      : (scope.includes('attend') || scope.includes('confirm') || scope.includes('yes')) ? 'attending'
      : scope.includes('vip') ? 'vip' : 'all';
    openCreate({
      type: pf.template_type === 'invitation' ? 'invitation' : 'email',
      templateId: String(pf.template_id),
      subject: pf.template_subject || undefined,
      segment,
      intent: `Event Readiness${payload.summary ? ` · ${payload.summary}` : (pf.template_name ? ` · ${pf.template_name}` : '')}`,
    });
    return undefined;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Header send-safety pill. Reflects live gate state; click opens the editor.
  const pill = sendMode ? MODE_PILL[sendMode] : null;

  return (
    <div className="ci-root">
      {/* title row */}
      <AdminPageHeader
        eyebrow="Guest Communications"
        title={title}
        tagline={sub}
        onDark
        right={!isFull && (
          <div className="ci-actions">
            {/* Send-safety mode pill — reads the live gate state; opens the editor. */}
            {modeLoading ? (
              <span className="ci-pill ci-pill-loading" title="Checking your send safety mode…">
                <Shield size={13} /> Checking…
              </span>
            ) : modeError ? (
              <button
                className="ci-pill ci-pill-error"
                onClick={() => setSafetyOpen(true)}
                title="Couldn't read your send mode. Open settings to view or set it."
              >
                <ShieldAlert size={13} /> Send safety
              </button>
            ) : (
              <button
                className={`ci-pill ${pill?.tone || 'ci-pill-blue'} ${safetyEnabled ? '' : 'ci-pill-off'}`}
                onClick={() => setSafetyOpen(true)}
                title={
                  safetyEnabled
                    ? `Send mode: ${pill?.label}. Click to view or change send-safety settings.`
                    : `Safety layer is OFF · ${pill?.label}. Click to manage.`
                }
              >
                {pill?.Icon ? <pill.Icon size={13} /> : <Shield size={13} />}
                {pill?.label || 'Send safety'}
                {!safetyEnabled && <span className="ci-pill-flag">safety off</span>}
              </button>
            )}
            <button
              className="ci-milo-switch"
              role="switch"
              aria-checked={!miloQuiet}
              onClick={() => setMiloQuiet((q) => !q)}
              title={miloQuiet ? 'Milo is resting — turn on' : 'Milo is on — turn off'}
            >
              {miloQuiet ? <Moon size={14} /> : <Sparkles size={14} />}
              <span>Milo {miloQuiet ? 'OFF' : 'ON'}</span>
              <span className={`ci-toggle ${miloQuiet ? 'ci-off' : ''}`} aria-hidden="true" />
            </button>
            <button className="ci-btn ci-gold" onClick={() => openCreate()}><Plus size={15} /> Create Communication</button>
          </div>
        )}
      />

      {/* KPI band — overview surfaces only (not on full-page screens) */}
      {!isFull && (
        <KpiBand
          onViewVips={() => setScreen('inbox')}
          onEditRecommendation={() => openCreate()}
          miloQuiet={miloQuiet}
          onWakeMilo={() => setMiloQuiet(false)}
        />
      )}

      {/* sub-tabs */}
      {!isFull && (
        <div className="ci-subtabs">
          {SUBTABS.map((t) => (
            <button
              key={t.key}
              className={`ci-stab ${screen === t.key ? 'ci-on' : ''}`}
              onClick={() => setScreen(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* screens */}
      {screen === 'inbox' && (
        <InboxScreen
          guests={guests}
          onMessagesChanged={onMessagesChanged}
          miloQuiet={miloQuiet}
          onWakeMilo={() => setMiloQuiet(false)}
        />
      )}
      {screen === 'outbox' && <OutboxScreen onUseAgain={openCreate} />}
      {screen === 'templates' && <TemplatesScreen />}
      {screen === 'automations' && <AutomationsScreen />}
      {screen === 'analytics' && <AnalyticsScreen />}
      {screen === 'create' && <CreateScreen onBack={backToTabs} prefill={createPrefill} />}
      {/* View Journey is now a top-level tab. JourneyScreen has its own guest
          picker, so we no longer seed initialGuestId; its built-in "Back"
          returns to Guest Communications. */}
      {screen === 'journey' && (
        <JourneyScreen guests={guests} initialGuestId={null} onBack={() => setScreen('inbox')} onPrepareSend={openCreate} />
      )}

      {/* Send-safety settings editor (modal). Refreshes the header pill on save. */}
      <SafetySettingsModal
        open={safetyOpen}
        onClose={() => setSafetyOpen(false)}
        onSaved={(s) => {
          setSendMode(['hold', 'test', 'live'].includes(s?.send_mode) ? s.send_mode : 'hold');
          setSafetyEnabled(!!s?.safety_enabled);
        }}
      />
    </div>
  );
}
